import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { APP_TAGLINE, LEAGUE_NAME, LOGO_SRC } from '../constants/branding'
import { useSession } from '../context/SessionContext'
import { useAsync } from '../hooks/useAsync'
import { fetchSeasons } from '../services/league'
import { formatSeasonStatus } from './ui'

const PRIMARY_NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/updates', label: 'Updates' },
  { to: '/standings', label: 'Standings' },
  { to: '/stats', label: 'Stats' },
  { to: '/playoffs', label: 'Playoffs' },
  { to: '/hall-of-champions', label: 'Hall of Champions' },
  { to: '/historical', label: 'Historical' },
  { to: '/nerd-data', label: 'Nerd Data' },
  { to: '/games', label: 'Games' },
  { to: '/upload', label: 'Upload' },
]

/** App chrome: sticky header, primary navigation, and footer. */
export default function Layout() {
  const { user, isAdmin } = useSession()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const seasons = useAsync(fetchSeasons, [])
  const currentSeason = seasons.data?.find(season => season.isCurrent)

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const linkClass = ({ isActive }: { isActive: boolean }): string =>
    isActive ? 'nav__link nav__link--active' : 'nav__link'

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="site-header__inner">
          <NavLink to="/" className="brand">
            <img src={LOGO_SRC} alt={`${LEAGUE_NAME} logo`} />
            <span className="brand__text">
              <span className="brand__name">{LEAGUE_NAME}</span>
              <span className="brand__tagline">{APP_TAGLINE}</span>
            </span>
          </NavLink>

          <button
            type="button"
            className="nav-toggle"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation"
            onClick={() => setMenuOpen(open => !open)}
          >
            ☰
          </button>

          <nav className={menuOpen ? 'nav nav--open' : 'nav'}>
            {PRIMARY_NAV.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `${linkClass({ isActive })} nav__link--admin`
                }
              >
                Admin
              </NavLink>
            )}
            <NavLink to="/account" className={linkClass}>
              {user ? user.displayName : 'Sign in'}
            </NavLink>
          </nav>
        </div>
      </header>

      <main className="page">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <span>
            {LEAGUE_NAME} — {APP_TAGLINE} league
            {' · '}
            <Link to="/rules" className="site-footer__link">
              Rules
            </Link>
            {' · '}
            <Link to="/changelog" className="site-footer__link">
              Changelog
            </Link>
          </span>
          {currentSeason && (
            <span>
              Season {currentSeason.number} · {formatSeasonStatus(currentSeason.status)}
            </span>
          )}
        </div>
      </footer>
    </div>
  )
}
